<template>
  <div class="tw-flex tw-justify-center">
    <button
      type="button"
      class="btn btn-light tw-relative"
      @click="togglePrelistenPlayout(prelistenURL)"
    >
      <icon-openmoji-stop-button v-if="isPlaying" class="tw-text-lg" />
      <icon-openmoji-jar-with-orange-content
        v-else-if="isLoading"
        class="tw-text-lg tw-animate-spin"
      />
      <icon-openmoji-play-button v-else class="tw-text-lg" />
    </button>
  </div>
</template>

<script lang="ts" setup>
import { parseISO } from 'date-fns'
import { computed, defineComponent, ref } from 'vue'
import { addSeconds, createURLBuilder } from './util'

defineComponent({
  inheritAttrs: false,
})

const props = defineProps<{
  modelValue: string
  apiBase: string
  startOffset: number
  endOffset: number
}>()

const buildURL = createURLBuilder(props.apiBase, true)
const isPlaying = ref(false)
const isLoading = ref(false)

const audio = new Audio()

// Move to UTILS
function pad(value: string | number, num = 2) {
  return String(value).padStart(num, '0')
}

function getTimezoneOffset(date?: Date) {
  const offset = (date ?? new Date()).getTimezoneOffset()
  const absOffset = Math.abs(offset)
  const minutes = absOffset % 60
  const hours = (absOffset - minutes) / 60
  const sign = offset <= 0 ? '+' : '-'
  return `${sign}${pad(hours)}:${pad(minutes)}`
}

// Move to UTILS
function parseLocalISO(date: string) {
  const timezoneOffset = getTimezoneOffset()
  return parseISO(date + timezoneOffset)
}

const startTimeISO = computed(() => {
  const startTime = addSeconds(parseLocalISO(props.modelValue), props.startOffset)
  // Replace Z with +00:00 because the backend doesn’t like it
  return startTime.toISOString().replace(/Z$/, '+00:00')
})

const endTimeISO = computed(() => {
  const endTime = addSeconds(parseLocalISO(props.modelValue), props.endOffset)
  // Replace Z with +00:00 because the backend doesn’t like it
  return endTime.toISOString().replace(/Z$/, '+00:00')
})

const prelistenURL = computed(() => {
  const query = new URLSearchParams({
    start: startTimeISO.value,
    end: endTimeISO.value,
    archive_id: '0',
  })
  return buildURL('prelisten', query)
})

audio.addEventListener('ended', () => {
  isPlaying.value = false
})

audio.addEventListener('loadstart', () => {
  isLoading.value = true
})

audio.addEventListener('pause', () => {
  isPlaying.value = false
  isLoading.value = false
})

audio.addEventListener('playing', () => {
  isPlaying.value = true
  isLoading.value = false
})

function togglePrelistenPlayout(url: string) {
  if (audio.paused === false) {
    audio.pause()
    audio.currentTime = 0
  } else {
    audio.src = url
    audio.play()
  }
}
</script>
